import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
  Alert,
  AlertIcon,
  useColorModeValue,
  Divider
} from '@chakra-ui/react';
import SEO from '../SEO';
import { generateSEOMetadata, staticPages } from '../../utils/seo';

const CookiePolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  // Generate SEO metadata for cookie policy page
  const seoMetadata = generateSEOMetadata.staticPage(
    staticPages['cookie-policy'].name,
    staticPages['cookie-policy'].url,
    staticPages['cookie-policy'].description
  );

  return (
    <>
      <SEO {...seoMetadata} />
      <Box bg={useColorModeValue('gray.50', 'gray.900')} py={12}>
        <Container maxW="4xl" bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
          <VStack spacing={8} align="stretch">
            <Box textAlign="center">
              <Heading as="h1" size="xl" mb={2} color="#4CAF50">
                Twygs Cookie Policy
              </Heading>
              <Text color="gray.500" fontSize="sm">
                <strong>Effective Date:</strong> {currentDate}
              </Text>
              <Text color="gray.500" fontSize="sm">
                <strong>Last Updated:</strong> {currentDate}
              </Text>
            </Box>

            <Box
              p={5}
              bg={useColorModeValue('green.50', 'green.900')}
              borderLeft="4px solid"
              borderColor="#4CAF50"
              borderRadius="md"
            >
              <Text>
                This Cookie Policy explains how Twygs ("Company," "we," "us," and "our") uses
                cookies and similar technologies to recognize you when you visit our platform. It
                explains what these technologies are and why we use them, as well as your rights to
                control our use of them.
              </Text>
            </Box>

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                1. Introduction
              </Heading>
              <Text color={textColor}>
                In some cases, we may use cookies to collect personal information, or information
                that becomes personal when combined with other data. This policy provides you with
                clear and comprehensive information about the cookies we use and the purposes for
                using them.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                2. What Are Cookies?
              </Heading>
              <Text color={textColor} mb={4}>
                Cookies are small data files that are placed on your computer or mobile device when
                you visit a website. Cookies are widely used by website owners to make their
                websites work more efficiently and to provide reporting information.
              </Text>
              <Text color={textColor} mb={4}>
                Cookies set by the website owner (in this case, Twygs) are called "first-party
                cookies." Cookies set by parties other than the website owner are called
                "third-party cookies." Third-party cookies enable third-party features or
                functionality to be provided on or through the website (e.g., advertising,
                interactive content, and analytics). The parties that set these third-party cookies
                can recognize your computer both when it visits the website in question and also
                when it visits certain other websites.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                3. Why Do We Use Cookies?
              </Heading>
              <Text color={textColor} mb={4}>
                We use first- and third-party cookies for several reasons:
              </Text>

              <VStack spacing={6} align="stretch" mt={4}>
                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.1 Essential Cookies
                  </Heading>
                  <Text color={textColor} mb={2}>
                    Some cookies are required for technical reasons in order for our Platform to
                    operate. We refer to these as "essential" or "strictly necessary" cookies. They
                    include:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Authentication cookies that identify you when you log into our Platform
                    </ListItem>
                    <ListItem>
                      Session cookies that enable the Platform to remember choices you make
                    </ListItem>
                    <ListItem>
                      Security cookies that help detect and prevent security risks
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.2 Functional Cookies
                  </Heading>
                  <Text color={textColor} mb={2}>
                    These cookies enable us to provide enhanced functionality and personalization:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>Remembering your preferences and settings</ListItem>
                    <ListItem>Remembering your content engagement history</ListItem>
                    <ListItem>Enabling personalized content recommendations</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.3 Analytics Cookies
                  </Heading>
                  <Text color={textColor} mb={2}>
                    These cookies help us understand how visitors interact with our Platform:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      Collecting information about which pages you visit and how you use the
                      Platform
                    </ListItem>
                    <ListItem>
                      Helping us understand user behavior and improve our Platform
                    </ListItem>
                    <ListItem>Measuring the effectiveness of our content and features</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    3.4 Advertising Cookies
                  </Heading>
                  <Text color={textColor} mb={2}>
                    These cookies are used to make advertising messages more relevant to you:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>Displaying ads that are relevant to your interests</ListItem>
                    <ListItem>Measuring the effectiveness of our advertising campaigns</ListItem>
                    <ListItem>
                      Supporting our ad-based revenue model that enables content creators to earn
                      from their work
                    </ListItem>
                  </UnorderedList>
                </Box>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                4. How Can I Control Cookies?
              </Heading>
              <Text color={textColor} mb={4}>
                You have the right to decide whether to accept or reject cookies. You can exercise
                your cookie preferences in several ways:
              </Text>

              <VStack spacing={6} align="stretch" mt={4}>
                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    4.1 Cookie Consent Manager
                  </Heading>
                  <Text color={textColor} mb={2}>
                    You can use our Cookie Consent Manager to select which categories of cookies you
                    accept or reject. Essential cookies cannot be rejected as they are strictly
                    necessary to provide you with services. The Cookie Consent Manager can be found
                    in the notification banner when you first visit our Platform.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    4.2 Browser Controls
                  </Heading>
                  <Text color={textColor} mb={2}>
                    Most web browsers allow you to control cookies through their settings. These
                    settings are typically found in the "options" or "preferences" menu of your
                    browser. You can set your browser to:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>Refuse all cookies</ListItem>
                    <ListItem>Accept only first-party cookies</ListItem>
                    <ListItem>Delete specific cookies</ListItem>
                    <ListItem>Block cookies from specific websites</ListItem>
                  </UnorderedList>
                  <Text color={textColor} mt={2}>
                    Here's how to manage cookies in popular browsers:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      <Link
                        color="#4CAF50"
                        href="https://support.google.com/chrome/answer/95647"
                        isExternal
                      >
                        Chrome
                      </Link>
                    </ListItem>
                    <ListItem>
                      <Link
                        color="#4CAF50"
                        href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d"
                        isExternal
                      >
                        Internet Explorer
                      </Link>
                    </ListItem>
                    <ListItem>
                      <Link
                        color="#4CAF50"
                        href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
                        isExternal
                      >
                        Firefox
                      </Link>
                    </ListItem>
                    <ListItem>
                      <Link
                        color="#4CAF50"
                        href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                        isExternal
                      >
                        Safari
                      </Link>
                    </ListItem>
                    <ListItem>
                      <Link
                        color="#4CAF50"
                        href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                        isExternal
                      >
                        Edge
                      </Link>
                    </ListItem>
                    <ListItem>
                      <Link
                        color="#4CAF50"
                        href="https://help.opera.com/en/latest/web-preferences/#cookies"
                        isExternal
                      >
                        Opera
                      </Link>
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    4.3 Opt-Out of Targeted Advertising
                  </Heading>
                  <Text color={textColor} mb={2}>
                    Most advertising networks offer you a way to opt out of targeted advertising.
                    You can find more information at:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>
                      <Link color="#4CAF50" href="https://www.aboutads.info/choices/" isExternal>
                        Digital Advertising Alliance
                      </Link>
                    </ListItem>
                    <ListItem>
                      <Link color="#4CAF50" href="https://youradchoices.ca/" isExternal>
                        Digital Advertising Alliance of Canada
                      </Link>
                    </ListItem>
                    <ListItem>
                      <Link color="#4CAF50" href="https://www.youronlinechoices.com/" isExternal>
                        European Interactive Digital Advertising Alliance
                      </Link>
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Alert
                  status="info"
                  variant="left-accent"
                  borderLeftColor="#4CAF50"
                  bg={useColorModeValue('green.50', 'green.900')}
                  mt={4}
                >
                  <AlertIcon color="#4CAF50" />
                  <Text fontWeight="medium">
                    Please note that if you choose to reject cookies, you may still use our
                    Platform, though your access to some functionality and areas may be restricted.
                  </Text>
                </Alert>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                5. Other Tracking Technologies
              </Heading>
              <Text color={textColor} mb={4}>
                Cookies are not the only way to recognize or track visitors to a website. We may use
                other similar technologies from time to time, such as:
              </Text>

              <VStack spacing={6} align="stretch" mt={4}>
                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    5.1 Web Beacons
                  </Heading>
                  <Text color={textColor} mb={2}>
                    Also known as "tracking pixels" or "clear gifs," these are tiny graphics files
                    that contain a unique identifier that enables us to recognize when someone has
                    visited our Platform or opened an email from us. This allows us to:
                  </Text>
                  <UnorderedList spacing={2} pl={4}>
                    <ListItem>Monitor traffic patterns from one page to another</ListItem>
                    <ListItem>
                      Understand whether you have come to our Platform from an online advertisement
                    </ListItem>
                    <ListItem>Improve site performance</ListItem>
                    <ListItem>Measure the success of email marketing campaigns</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={2} color="#70BF73">
                    5.2 Local Storage
                  </Heading>
                  <Text color={textColor} mb={2}>
                    We may use local storage technologies (such as HTML5 localStorage) to store
                    content information and preferences. These technologies are similar to cookies
                    but can store larger amounts of data and are stored on your device in a
                    different location than cookies.
                  </Text>
                </Box>
              </VStack>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                6. Targeted Advertising
              </Heading>
              <Text color={textColor} mb={4}>
                Third parties may serve cookies on your computer or mobile device to serve
                advertising through our Platform. These companies may use information about your
                visits to this and other websites to provide relevant advertisements about goods and
                services that you may be interested in.
              </Text>
              <Text color={textColor} mb={4}>
                They may also employ technology that is used to measure the effectiveness of
                advertisements. They can accomplish this by using cookies or web beacons to collect
                information about your visits to this and other sites in order to provide relevant
                advertisements about goods and services of potential interest to you.
              </Text>
              <Text color={textColor}>
                The information collected through this process does not enable us or them to
                identify your name, contact details, or other personally identifying information
                unless you choose to provide it.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                7. Cookie Policy Updates
              </Heading>
              <Text color={textColor}>
                We may update this Cookie Policy from time to time to reflect changes in our
                practices or for other operational, legal, or regulatory reasons. Please revisit
                this Cookie Policy regularly to stay informed about our use of cookies and related
                technologies. The date at the top of this Cookie Policy indicates when it was last
                updated.
              </Text>
            </Box>

            <Divider borderColor={borderColor} />

            <Box as="section">
              <Heading as="h2" size="lg" mb={4} color="#4CAF50">
                8. Contact Us
              </Heading>
              <Text color={textColor} mb={2}>
                If you have any questions about our use of cookies or other technologies, please
                contact us at:
              </Text>
              <Text color={textColor}>
                Email:{' '}
                <Link color="#4CAF50" href="mailto:hello@twygs.io">
                  hello@twygs.io
                </Link>
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default CookiePolicy;
